import { useState } from 'react';
import type { ReactNode } from 'react';
import { AUTONOMY_LEVELS, FORMALITIES, VOICE_MODES } from '@yourstore/concierge-client';
import type { AgentSettingsPatch } from '@yourstore/concierge-client';
import { useAction, useApi, useRead } from '../../context';
import type { ReadState } from '../../context';
import { Confirm, Failure, Loading } from '../../bits';
import { Chip, Panel, Switch } from '../../kit';
import type { ChipTone } from '../../kit';
import { useLocale, useT } from '../../../messages';
import type { T } from '../../../messages';
import type { ApiKeyRow, ConciergePartner, ConciergeRead, WebhookEndpoint } from '../../../wire';
import { Dialog } from './Dialog';
import { KeyRow, useKeyRevoke } from './KeysCard';
import { centsToInput, inputToCents, relative } from './words';

/**
 * **AI concierge** — one card with a to-do list, instead of a status line of
 * partner tokens, provider codes and version numbers.
 *
 * It answers, in order: is it on, what is it doing, what is left to do, and
 * then the controls. The facts the old card printed as tokens are all still
 * here as sentences — `paused` is "Paused: it still answers and takes
 * messages", a provider that is not `connected` is "It can't use the key it
 * holds", a catalog version and its sync time is "Services last sent 2 minutes
 * ago" — and the concierge's own key and the address its updates go to are
 * shown **here** and nowhere else on the tab.
 *
 * ## The two things this card is careful about (unchanged)
 *
 * **1. There is no key on it, anywhere.** Connecting and re-keying hand the API
 * key and the signing secret over machine to machine; a console that displayed
 * either would put it back in a browser's memory, a screenshot and somebody's
 * clipboard. What is shown is the key's prefix, which is not a secret, and the
 * *name* of where the signing secret is kept, which is not one either.
 *
 * **2. It offers no editor for anything the business owns.** No hours, prices,
 * policies or questions — they reach the agent from the business's own setup.
 * "What the agent does" edits only the agent's behaviour, and the patch type is
 * `AgentSettingsPatch`, whose `.strict()` makes anything else a compile error.
 *
 * ## Names, not ids
 *
 * The per-service switches are named from the business's own catalog, which
 * this console may read (the old card's claim that it could not has not been
 * true since the catalog grant). A service the catalog no longer names still
 * renders, by its id, because the partner's list is theirs and may hold one the
 * business has retired — and a switch with no label is worse than the id.
 *
 * Every write that changes what the concierge does for the business confirms
 * first (plan §1, principle 5): connecting, pausing, re-keying, and pausing
 * with a revoke. Sync and Resume do not — the first changes nothing the
 * business would notice, the second undoes a pause.
 */
export function ConciergeCard({
  tenantId,
  name,
  read,
  conciergeKeys,
  endpoint,
  onChanged,
}: {
  tenantId: string;
  name: string;
  read: ReadState<ConciergeRead>;
  /** The concierge's working keys, off the tenant read. */
  conciergeKeys: readonly ApiKeyRow[];
  /** Where the concierge's booking updates go, off the tenant read. */
  endpoint: WebhookEndpoint | null;
  onChanged: () => void;
}): ReactNode {
  const t = useT();
  const state = read.value;

  return (
    <Panel
      title={t('connections.concierge.title')}
      className="conn-card"
      actions={state === null ? undefined : <ConciergeChip state={state} />}
    >
      {read.failure !== null ? <Failure failure={read.failure} /> : null}
      {state === null ? (
        read.loading ? (
          <Loading what={t('connections.loading')} />
        ) : null
      ) : (
        <ConciergeBody
          tenantId={tenantId}
          name={name}
          state={state}
          conciergeKeys={conciergeKeys}
          endpoint={endpoint}
          onChanged={onChanged}
        />
      )}
    </Panel>
  );
}

function ConciergeChip({ state }: { state: ConciergeRead }): ReactNode {
  const t = useT();
  const { tone, label } = chipOf(t, state);
  return <Chip tone={tone}>{label}</Chip>;
}

function chipOf(t: T, state: ConciergeRead): { tone: ChipTone | undefined; label: string } {
  if (!state.configured)
    return { tone: 'plain', label: t('connections.concierge.chip.unavailable') };
  const partner = state.concierge;
  if (partner === null) {
    return state.unreachable === null
      ? { tone: undefined, label: t('connections.concierge.chip.off') }
      : { tone: 'warn', label: t('connections.concierge.chip.unknown') };
  }
  if (partner.status === 'paused')
    return { tone: 'warn', label: t('connections.concierge.chip.paused') };
  if (partner.provider?.status != null && partner.provider.status !== 'connected') {
    return { tone: 'danger', label: t('connections.concierge.chip.broken') };
  }
  const todo = partner.missing?.length ?? 0;
  if (todo > 0)
    return { tone: 'warn', label: t('connections.concierge.chip.todo', { count: todo }) };
  return { tone: 'ok', label: t('connections.concierge.chip.on') };
}

type Asking = 'connect' | 'pause' | 'rekey' | 'revoke' | null;

function ConciergeBody({
  tenantId,
  name,
  state,
  conciergeKeys,
  endpoint,
  onChanged,
}: {
  tenantId: string;
  name: string;
  state: ConciergeRead;
  conciergeKeys: readonly ApiKeyRow[];
  endpoint: WebhookEndpoint | null;
  onChanged: () => void;
}): ReactNode {
  const api = useApi();
  const t = useT();
  const action = useAction();
  const [asking, setAsking] = useState<Asking>(null);
  const [result, setResult] = useState<string | null>(null);
  const [warnings, setWarnings] = useState<string[]>([]);

  /**
   * The honest off state: a sentence, and **no button**. A disabled button
   * would still be a button an operator clicks; a sentence is the thing that
   * gets read. What the deployment lacks is one click deeper, for whoever is
   * going to fix it.
   */
  if (!state.configured) {
    return (
      <>
        <p className="muted">{t('connections.concierge.unavailable')}</p>
        {(state.missing_config ?? []).length > 0 ? (
          <details className="conn-more">
            <summary>{t('connections.concierge.unavailableWhat')}</summary>
            <p className="mono conn-small">{(state.missing_config ?? []).join(', ')}</p>
          </details>
        ) : null}
      </>
    );
  }

  const partner = state.concierge;
  const paused = partner?.status === 'paused';

  /** Run a write; `asked` closes its confirm on success. */
  const run = async (work: () => Promise<void>): Promise<void> => {
    setResult(null);
    setWarnings([]);
    const ok = await action.run(work);
    if (ok) {
      setAsking(null);
      onChanged();
    }
  };

  const enable = (): Promise<void> =>
    run(async () => {
      const answer = await api.enableConcierge(tenantId);
      setResult(t('connections.concierge.connected'));
      setWarnings(answer.warnings);
    });

  const confirmOf = (which: Exclude<Asking, null>): ReactNode => {
    const common = {
      busy: action.busy,
      failure: action.failure,
      onCancel: () => {
        action.clear();
        setAsking(null);
      },
    };
    switch (which) {
      case 'connect':
        return (
          <Confirm
            {...common}
            tone="primary"
            title={t('connections.concierge.connectTitle', { name })}
            consequence={t('connections.concierge.connectConsequence')}
            confirmLabel={t('connections.concierge.connectLabel')}
            onConfirm={() => void enable()}
          />
        );
      case 'rekey':
        return (
          <Confirm
            {...common}
            tone="primary"
            title={t('connections.concierge.rekeyTitle')}
            consequence={t('connections.concierge.rekeyConsequence')}
            confirmLabel={t('connections.concierge.rekeyLabel')}
            onConfirm={() => void enable()}
          />
        );
      case 'pause':
        return (
          <Confirm
            {...common}
            title={t('connections.concierge.pauseTitle')}
            consequence={t('connections.concierge.pauseConsequence', { name })}
            confirmLabel={t('connections.concierge.pauseLabel')}
            onConfirm={() =>
              void run(async () => {
                await api.conciergeStatus(tenantId, 'paused');
              })
            }
          />
        );
      case 'revoke':
        return (
          <Confirm
            {...common}
            title={t('connections.concierge.revokeTitle')}
            consequence={t('connections.concierge.revokeConsequence')}
            confirmLabel={t('connections.concierge.revokeLabel')}
            onConfirm={() =>
              void run(async () => {
                await api.disableConcierge(tenantId);
                setResult(t('connections.concierge.revoked'));
              })
            }
          />
        );
    }
  };

  const ask = (which: Exclude<Asking, null>): void => {
    action.clear();
    setAsking(which);
  };

  const outcome = (
    <>
      {asking === null && action.failure !== null ? <Failure failure={action.failure} /> : null}
      {result !== null ? <p className="ok">{result}</p> : null}
      {warnings.map((w) => (
        <p key={w} className="warn">
          {w}
        </p>
      ))}
    </>
  );

  return (
    <>
      {state.unreachable !== null ? (
        <p className="warn">
          {t('connections.concierge.unreachable', { message: state.unreachable })}
        </p>
      ) : null}

      {partner === null ? (
        <>
          <p className="muted">{t('connections.concierge.pitch')}</p>
          <ul className="conn-todo">
            <li>{t('connections.concierge.todo.agree')}</li>
            <li>{t('connections.concierge.todo.services')}</li>
            <li>{t('connections.concierge.todo.phone')}</li>
          </ul>
          {outcome}
          <div className="row-actions">
            <button
              type="button"
              className="button button--primary button--sm"
              onClick={() => ask('connect')}
              disabled={action.busy}
            >
              {t('connections.concierge.connect')}
            </button>
          </div>
        </>
      ) : (
        <>
          <Doing name={name} partner={partner} />
          <StillToDo partner={partner} />
          {outcome}
          <div className="row-actions">
            <button
              type="button"
              className="button button--sm"
              disabled={action.busy}
              onClick={() =>
                void run(async () => {
                  await api.conciergeSync(tenantId);
                  setResult(t('connections.concierge.synced.done'));
                })
              }
            >
              {t('connections.concierge.sync')}
            </button>
            {paused ? (
              <button
                type="button"
                className="button button--sm"
                disabled={action.busy}
                onClick={() =>
                  void run(async () => {
                    await api.conciergeStatus(tenantId, 'enabled');
                    setResult(t('connections.concierge.resumed'));
                  })
                }
              >
                {t('connections.concierge.resume')}
              </button>
            ) : (
              <button type="button" className="button button--sm" onClick={() => ask('pause')}>
                {t('connections.concierge.pause')}
              </button>
            )}
            <button type="button" className="button button--sm" onClick={() => ask('rekey')}>
              {t('connections.concierge.rekey')}
            </button>
            <button
              type="button"
              className="button button--sm button--danger"
              onClick={() => ask('revoke')}
            >
              {t('connections.concierge.revoke')}
            </button>
          </div>
          <p className="muted conn-small">{t('connections.concierge.syncNote')}</p>

          <Wiring
            tenantId={tenantId}
            state={state}
            conciergeKeys={conciergeKeys}
            endpoint={endpoint}
            onChanged={onChanged}
          />
          <AgentSummary tenantId={tenantId} partner={partner} onChanged={onChanged} />
          <ServiceSwitches tenantId={tenantId} partner={partner} onChanged={onChanged} />
        </>
      )}

      {asking === null ? null : confirmOf(asking)}
    </>
  );
}

/* ------------------------------------------------------------------ */
/* What it is doing, and what is left                                  */
/* ------------------------------------------------------------------ */

function Doing({ name, partner }: { name: string; partner: ConciergePartner }): ReactNode {
  const t = useT();
  const locale = useLocale();
  const broken = partner.provider?.status != null && partner.provider.status !== 'connected';
  const when = (at: string | null | undefined): string =>
    at == null ? t('connections.concierge.never') : relative(at, locale);

  return (
    <div className="conn-doing">
      <p>
        {partner.status === 'paused'
          ? t('connections.concierge.doing.paused')
          : broken
            ? t('connections.concierge.doing.broken')
            : t('connections.concierge.doing.on', { name })}
      </p>
      {broken && partner.provider?.detail != null ? (
        <p className="muted conn-small">
          {/* Their sentence, as it arrived. */}
          {t('connections.concierge.doing.brokenDetail', { detail: partner.provider.detail })}
        </p>
      ) : null}
      <p className="muted conn-small">
        {t('connections.concierge.synced', {
          services: when(partner.catalog_source?.synced_at),
          details: when(partner.profile_source?.synced_at),
        })}
      </p>
    </div>
  );
}

type MissingKey =
  | 'connections.concierge.missing.provider_connection'
  | 'connections.concierge.missing.catalog'
  | 'connections.concierge.missing.phone_number'
  | 'connections.concierge.missing.owner_number';

const MISSING: Record<string, MissingKey> = {
  provider_connection: 'connections.concierge.missing.provider_connection',
  catalog: 'connections.concierge.missing.catalog',
  phone_number: 'connections.concierge.missing.phone_number',
  owner_number: 'connections.concierge.missing.owner_number',
};

/**
 * The partner's `missing`, as the to-do list it was handed over as. A phone
 * number is the one item nothing on this side can fix, so it links out. A
 * key this console has never heard of is shown as itself — their list grows on
 * their release schedule.
 */
function StillToDo({ partner }: { partner: ConciergePartner }): ReactNode {
  const t = useT();
  const missing = partner.missing ?? [];
  if (missing.length === 0) return null;
  const consoleUrl = partner.console_url ?? null;

  return (
    <div>
      <h4 className="conn-sub">{t('connections.concierge.stillTodo')}</h4>
      <ul className="conn-todo">
        {missing.map((key) => {
          const sentence = MISSING[key];
          return (
            <li key={key}>
              {sentence === undefined ? key : t(sentence)}
              {key === 'phone_number' && consoleUrl !== null ? (
                <>
                  {' '}
                  <a href={consoleUrl} target="_blank" rel="noreferrer">
                    {t('connections.concierge.openConsole')}
                  </a>
                </>
              ) : null}
            </li>
          );
        })}
      </ul>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Its key and update address                                          */
/* ------------------------------------------------------------------ */

function Wiring({
  tenantId,
  state,
  conciergeKeys,
  endpoint,
  onChanged,
}: {
  tenantId: string;
  state: ConciergeRead;
  conciergeKeys: readonly ApiKeyRow[];
  endpoint: WebhookEndpoint | null;
  onChanged: () => void;
}): ReactNode {
  const t = useT();
  const revoke = useKeyRevoke(tenantId, onChanged);
  const url = endpoint?.url ?? state.local.webhook_url;
  const secretRef = endpoint?.secret_ref ?? state.local.webhook_secret_ref;
  const failing = endpoint?.failing ?? state.local.webhook_failing;
  const active = endpoint?.active ?? state.local.webhook_active;

  return (
    <div>
      <h4 className="conn-sub">{t('connections.concierge.wiring')}</h4>
      <dl className="conn-facts">
        <div>
          <dt>{t('connections.concierge.itsKeys')}</dt>
          <dd>
            {conciergeKeys.length === 0 ? (
              <span className="muted">{t('connections.concierge.noKey')}</span>
            ) : (
              <ul className="conn-list">
                {conciergeKeys.map((k) => (
                  <KeyRow key={k.key_id} row={k} onRevoke={revoke.ask} />
                ))}
              </ul>
            )}
            {revoke.failure}
          </dd>
        </div>
        <div>
          <dt>{t('connections.concierge.updatesTo')}</dt>
          <dd>
            {url === null ? (
              <span className="muted">{t('connections.concierge.noAddress')}</span>
            ) : (
              <>
                <span className="mono wrap">{url}</span>
                {failing ? (
                  <span className="warn conn-inline">
                    {t('connections.concierge.addressFailing')}
                  </span>
                ) : null}
                {active ? null : (
                  <span className="muted conn-inline">
                    {t('connections.concierge.addressStopped')}
                  </span>
                )}
              </>
            )}
          </dd>
        </div>
        {secretRef === null ? null : (
          <div>
            <dt>{t('connections.concierge.secretAt')}</dt>
            <dd className="mono wrap conn-small">{secretRef}</dd>
          </div>
        )}
        {state.local.site_key === null ? null : (
          <div>
            <dt>{t('connections.concierge.siteKey')}</dt>
            <dd>
              <span className="mono">{state.local.site_key}</span>{' '}
              <span className="muted conn-small">{t('connections.concierge.siteKeyNote')}</span>
            </dd>
          </div>
        )}
      </dl>
      {revoke.dialog}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* What the agent does                                                 */
/* ------------------------------------------------------------------ */

function autonomyName(t: T, level: string): string {
  switch (level) {
    case 'conservative':
      return t('connections.agent.autonomy.conservative');
    case 'balanced':
      return t('connections.agent.autonomy.balanced');
    case 'autonomous':
      return t('connections.agent.autonomy.autonomous');
    default:
      return level;
  }
}

function autonomyWhat(t: T, level: string): string {
  switch (level) {
    case 'conservative':
      return t('connections.agent.autonomy.conservative.what');
    case 'balanced':
      return t('connections.agent.autonomy.balanced.what');
    case 'autonomous':
      return t('connections.agent.autonomy.autonomous.what');
    default:
      return '';
  }
}

function formalityName(t: T, value: string): string {
  switch (value) {
    case 'casual':
      return t('connections.agent.formality.casual');
    case 'warm':
      return t('connections.agent.formality.warm');
    case 'professional':
      return t('connections.agent.formality.professional');
    default:
      return value;
  }
}

function voiceModeName(t: T, value: string): string {
  switch (value) {
    case 'disabled':
      return t('connections.agent.voiceMode.disabled');
    case 'ai_first':
      return t('connections.agent.voiceMode.ai_first');
    case 'owner_first':
      return t('connections.agent.voiceMode.owner_first');
    default:
      return value;
  }
}

function AgentSummary({
  tenantId,
  partner,
  onChanged,
}: {
  tenantId: string;
  partner: ConciergePartner;
  onChanged: () => void;
}): ReactNode {
  const t = useT();
  const [editing, setEditing] = useState(false);
  const [saved, setSaved] = useState<string | null>(null);
  const settings = partner.settings ?? {};
  const autonomy = settings.autonomy ?? null;

  return (
    <div>
      <h4 className="conn-sub">{t('connections.agent.title')}</h4>
      {autonomy === null ? (
        <p className="muted">{t('connections.agent.autonomy.notSet')}</p>
      ) : (
        <p>
          <strong>{autonomyName(t, autonomy)}</strong>{' '}
          <span className="muted">{autonomyWhat(t, autonomy)}</span>
        </p>
      )}
      {settings.greeting == null || settings.greeting === '' ? null : (
        <p className="muted conn-small">
          {t('connections.agent.summaryGreeting', { greeting: settings.greeting })}
        </p>
      )}
      {saved !== null ? <p className="ok">{saved}</p> : null}
      <div className="row-actions">
        <button
          type="button"
          className="button button--sm"
          onClick={() => {
            setSaved(null);
            setEditing(true);
          }}
        >
          {t('connections.agent.edit')}
        </button>
      </div>
      {editing ? (
        <AgentDialog
          tenantId={tenantId}
          partner={partner}
          onSaved={() => {
            setSaved(t('connections.agent.saved'));
            setEditing(false);
            onChanged();
          }}
          onClose={() => setEditing(false)}
        />
      ) : null}
    </div>
  );
}

/**
 * **Only changed fields are sent.** A form that re-asserted everything would
 * turn "I fixed a typo in the greeting" into a write that also re-states an
 * autonomy level somebody changed in the other console a minute ago — the same
 * rule the staff app's service editor follows, and the reason their `PATCH` is
 * partial in the first place.
 */
function AgentDialog({
  tenantId,
  partner,
  onSaved,
  onClose,
}: {
  tenantId: string;
  partner: ConciergePartner;
  onSaved: () => void;
  onClose: () => void;
}): ReactNode {
  const api = useApi();
  const t = useT();
  const action = useAction();
  const stored = partner.settings ?? {};

  const [autonomy, setAutonomy] = useState(stored.autonomy ?? '');
  const [ceiling, setCeiling] = useState(centsToInput(stored.autonomous_value_ceiling_cents));
  const [greeting, setGreeting] = useState(stored.greeting ?? '');
  const [voiceGreeting, setVoiceGreeting] = useState(stored.voice_greeting ?? '');
  const [character, setCharacter] = useState(stored.tone?.voice ?? '');
  const [formality, setFormality] = useState(stored.tone?.formality ?? '');
  const [avoid, setAvoid] = useState((stored.tone?.avoid ?? []).join('\n'));
  const [ownerNumber, setOwnerNumber] = useState(stored.owner_number ?? '');
  const [voiceMode, setVoiceMode] = useState(stored.voice_mode ?? '');
  const [said, setSaid] = useState<string | null>(null);

  const ceilingCents = inputToCents(ceiling);

  const patch = (): AgentSettingsPatch => {
    const out: Record<string, unknown> = {};
    if (autonomy !== '' && autonomy !== stored.autonomy) out['autonomy'] = autonomy;
    if (
      ceilingCents !== undefined &&
      ceilingCents !== (stored.autonomous_value_ceiling_cents ?? null)
    ) {
      out['autonomous_value_ceiling_cents'] = ceilingCents;
    }
    if (greeting !== (stored.greeting ?? '')) out['greeting'] = greeting === '' ? null : greeting;
    if (voiceGreeting !== (stored.voice_greeting ?? '')) {
      out['voice_greeting'] = voiceGreeting === '' ? null : voiceGreeting;
    }
    const avoidNow = avoid
      .split('\n')
      .map((w) => w.trim())
      .filter((w) => w !== '');
    const tone: Record<string, unknown> = {};
    if (character !== (stored.tone?.voice ?? '')) {
      tone['voice'] = character === '' ? null : character;
    }
    if (formality !== '' && formality !== stored.tone?.formality) tone['formality'] = formality;
    if (avoidNow.join('\n') !== (stored.tone?.avoid ?? []).join('\n')) tone['avoid'] = avoidNow;
    if (Object.keys(tone).length > 0) out['tone'] = tone;
    if (voiceMode !== '' && voiceMode !== stored.voice_mode) out['voice_mode'] = voiceMode;
    if (ownerNumber !== (stored.owner_number ?? '')) {
      out['owner_number'] = ownerNumber === '' ? null : ownerNumber;
    }
    return out as AgentSettingsPatch;
  };

  const save = async (): Promise<void> => {
    setSaid(null);
    if (ceilingCents === undefined) return;
    const body = patch();
    if (Object.keys(body).length === 0) {
      setSaid(t('connections.agent.nothingChanged'));
      return;
    }
    const ok = await action.run(async () => {
      await api.conciergeSettings(tenantId, body);
    });
    if (ok) onSaved();
  };

  return (
    <Dialog title={t('connections.agent.title')} onClose={onClose} busy={action.busy} wide>
      <form
        className="form"
        onSubmit={(e) => {
          e.preventDefault();
          void save();
        }}
      >
        <p className="muted conn-small">{t('connections.agent.note')}</p>

        <fieldset className="conn-choices">
          <legend>{t('connections.agent.autonomy')}</legend>
          {AUTONOMY_LEVELS.map((level) => (
            <label key={level} className="conn-choice">
              <input
                type="radio"
                name="autonomy"
                checked={autonomy === level}
                onChange={() => setAutonomy(level)}
              />
              <span>
                <strong>{autonomyName(t, level)}</strong>
                <span className="conn-choice__what">{autonomyWhat(t, level)}</span>
              </span>
            </label>
          ))}
        </fieldset>

        <div className="form__row">
          <label className="field">
            <span className="field__label">{t('connections.agent.ceiling')}</span>
            <input
              aria-label={t('connections.agent.ceiling')}
              inputMode="decimal"
              value={ceiling}
              onChange={(e) => setCeiling(e.target.value)}
              aria-invalid={ceilingCents === undefined}
            />
            <span className="field__hint">{t('connections.agent.ceilingHint')}</span>
            {ceilingCents === undefined ? (
              <span className="warn" role="alert">
                {t('connections.agent.ceilingInvalid')}
              </span>
            ) : null}
          </label>
          <label className="field">
            <span className="field__label">{t('connections.agent.ownerNumber')}</span>
            <input
              aria-label={t('connections.agent.ownerNumber')}
              value={ownerNumber}
              onChange={(e) => setOwnerNumber(e.target.value)}
              placeholder="+13405550100"
            />
            <span className="field__hint">{t('connections.agent.ownerNumberHint')}</span>
          </label>
        </div>

        <label className="field field--wide">
          <span className="field__label">{t('connections.agent.greeting')}</span>
          <input
            aria-label={t('connections.agent.greeting')}
            value={greeting}
            onChange={(e) => setGreeting(e.target.value)}
          />
        </label>
        <label className="field field--wide">
          <span className="field__label">{t('connections.agent.voiceGreeting')}</span>
          <input
            aria-label={t('connections.agent.voiceGreeting')}
            value={voiceGreeting}
            onChange={(e) => setVoiceGreeting(e.target.value)}
          />
          <span className="field__hint">{t('connections.agent.voiceGreetingHint')}</span>
        </label>
        <label className="field field--wide">
          <span className="field__label">{t('connections.agent.character')}</span>
          <input
            aria-label={t('connections.agent.character')}
            value={character}
            onChange={(e) => setCharacter(e.target.value)}
          />
        </label>

        <label className="field">
          <span className="field__label">{t('connections.agent.formality')}</span>
          <select
            aria-label={t('connections.agent.formality')}
            value={formality}
            onChange={(e) => setFormality(e.target.value)}
          >
            <option value="">{t('connections.agent.formality.unset')}</option>
            {FORMALITIES.map((f) => (
              <option key={f} value={f}>
                {formalityName(t, f)}
              </option>
            ))}
          </select>
        </label>

        <fieldset className="conn-choices">
          <legend>{t('connections.agent.voiceMode')}</legend>
          {VOICE_MODES.map((mode) => (
            <label key={mode} className="conn-choice">
              <input
                type="radio"
                name="voice-mode"
                checked={voiceMode === mode}
                onChange={() => setVoiceMode(mode)}
              />
              <span>{voiceModeName(t, mode)}</span>
            </label>
          ))}
        </fieldset>

        <label className="field field--wide">
          <span className="field__label">{t('connections.agent.avoid')}</span>
          <textarea
            aria-label={t('connections.agent.avoid')}
            value={avoid}
            onChange={(e) => setAvoid(e.target.value)}
            rows={3}
          />
          <span className="field__hint">{t('connections.agent.avoidHint')}</span>
        </label>

        {action.failure !== null ? <Failure failure={action.failure} /> : null}
        {said !== null ? <p className="muted">{said}</p> : null}

        <div className="modal__actions">
          <button type="button" className="button" onClick={onClose} disabled={action.busy}>
            {t('common.cancel')}
          </button>
          <button
            type="submit"
            className="button button--primary"
            disabled={action.busy || ceilingCents === undefined}
          >
            {action.busy ? t('common.saving') : t('connections.agent.save')}
          </button>
        </div>
      </form>
    </Dialog>
  );
}

/* ------------------------------------------------------------------ */
/* What the AI may book                                                */
/* ------------------------------------------------------------------ */

function ServiceSwitches({
  tenantId,
  partner,
  onChanged,
}: {
  tenantId: string;
  partner: ConciergePartner;
  onChanged: () => void;
}): ReactNode {
  const api = useApi();
  const t = useT();
  const action = useAction();
  const services = partner.settings?.services ?? [];

  /*
   * The names, from the business's own catalog. A failure is deliberately not
   * rendered: this card's subject is the agent, and the read is a courtesy —
   * without it the switches fall back to the partner's name, then the id.
   */
  const catalog = useRead(() => api.catalog(tenantId), [tenantId]);
  const locale = catalog.value?.default_locale ?? 'en';
  const names = new Map(
    (catalog.value?.items ?? []).map((item) => [
      item.item_id,
      item.name[locale] ?? Object.values(item.name)[0] ?? '',
    ]),
  );
  const named = names.size > 0;

  const toggle = async (id: string, next: boolean): Promise<void> => {
    const ok = await action.run(async () => {
      await api.conciergeSettings(tenantId, { services: [{ id, bookable_by_ai: next }] });
    });
    if (ok) onChanged();
  };

  return (
    <div>
      <h4 className="conn-sub">{t('connections.book.title')}</h4>
      <p className="muted conn-small">{t('connections.book.note')}</p>
      {services.length === 0 ? (
        <p className="empty">{t('connections.book.empty')}</p>
      ) : (
        <ul className="conn-list">
          {services.map((service) => {
            const label = names.get(service.id) ?? service.name ?? service.id;
            const retired = named && !names.has(service.id);
            return (
              <li key={service.id} className="conn-row">
                <div className="conn-row__main">
                  <span className={label === service.id ? 'mono' : undefined}>{label}</span>
                  {retired ? (
                    <span className="muted conn-small"> · {t('connections.book.retired')}</span>
                  ) : null}
                </div>
                <Switch
                  checked={service.bookable_by_ai}
                  disabled={action.busy}
                  label={t('connections.book.label', { service: label })}
                  onChange={(next) => void toggle(service.id, next)}
                />
              </li>
            );
          })}
        </ul>
      )}
      {action.failure !== null ? <Failure failure={action.failure} /> : null}
    </div>
  );
}
